import {Component, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {OrganizationService} from '@services/administration/organization/organization.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {Organization} from '@models/organization/organization';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {OrganizationEditComponent} from '@components/administration/organization/modal/organization-edit.component';
import {Cursor} from '@constants/administration/cursor';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {ModalService} from '@services/modal/modal.service';
import {ITEMS_PER_PAGE_OPTIONS} from '@constants/pagination/pagination.constant';
import {PaginateControlSettings} from '@models/paginate-control-settings';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';


@Component({
    selector: 'scitis-admin-organization',
    templateUrl: './organization.component.html',
    styleUrls: ['./organization.component.scss'],
    standalone: false
})
export class OrganizationComponent implements OnInit, OnDestroy {
  public isSuperAdminRole: boolean = false;
  public isAdminRole: boolean = false;
  public organizationsList: Organization[] = new Array<Organization>();
  public itemsPerPageOptions: number[] = ITEMS_PER_PAGE_OPTIONS;
  public rootOrganizationId: number = -1;
  public searchTerm: string = '';
  public organizationCount: number = 0;
  public paginateSettings: PaginateControlSettings = new PaginateControlSettings({
    id: 'organizationPaginationId',
  });
  public restrictOrganizationInteraction: boolean = false;
  //SORTING
  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  private CURRENT_PAGE_NAME = 'Organization';
  private searchSubject$: Subject<string> = new Subject<string>();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private cursors: Map<number, string> = new Map<number, string>();
  private sortValue: ElasticSearchSortValue = new ElasticSearchSortValue();

  constructor(
      private modalService: ModalService,
      private organizationService: OrganizationService,
      private loadingIndicator: LoadingIndicatorService,
      private firebaseAuthenticationService: FirebaseAuthenticationService,
      private notification: NotificationService,
      private translation: TranslateService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      private elasticSearchService: ElasticSearchService,
  ) {
    this.resetProperties();
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(({search}) => {
      this.searchTerm = search || '';
      this.searchSubject$.next(this.searchTerm);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.setAuthenticatedUserRoleAndOrganization();
    this.getRestrictedTenantInteraction();
    this.searchSubject$.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.searchTerm = value;
      this.paginateSettings = {
        ...this.paginateSettings,
        currentPage: 1,
      };
      this.setOrganizations();
    });
  }

  public onAddOrganization() {
    const modalRef = this.modalService.open(OrganizationEditComponent, {size: 'lg'});
    modalRef.componentInstance.isSuperAdminRole = this.isSuperAdminRole;
    modalRef.componentInstance.rootOrganizationId = this.rootOrganizationId;
    modalRef.componentInstance.organization = new Organization();
    modalRef.result.then((result) => {
      this.loadingIndicator.show();
      this.organizationService.saveOrganization(result).subscribe({
        next: (success) => {
          this.organizationCount++;
          this.paginateSettings = {
            ...this.paginateSettings,
            totalItems: this.organizationCount,
          };
          this.organizationsList.push(success);
          const message = this.translation.instant('Organization') + ' ' + success.name + ' ' +
              this.translation.instant('added') + '.';

          this.loadingIndicator.hide();
          this.notification.success(message, this.CURRENT_PAGE_NAME, false);
        }, error: (error) => {
          this.loadingIndicator.hide();
          this.notification.error(this.translation.instant('Error adding organization') + ': ' + result.name,
              error, this.CURRENT_PAGE_NAME, true);
        }
      });
    });
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.paginateSettings = {
      ...this.paginateSettings,
      itemsPerPage: itemsPerPage,
      currentPage: 1,
    };
    this.resetProperties();
    this.setOrganizations();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: pageNumber,
    };
    this.setOrganizations();
  }

  public onSearch({target}): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {search: target.value},
      queryParamsHandling: 'merge',
    });
  }

  public onDeleteOrganizationSuccess(organization: Organization): void {
    this.organizationCount--;
    this.paginateSettings = {
      ...this.paginateSettings,
      totalItems: this.organizationCount,
    };
    const index = this.organizationsList.indexOf(organization);
    if (index >= 0) {
      this.organizationsList.splice(index, 1);
    }
  }

  onColumnSort({column, direction}: SortEvent) {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.sortValue = this.tableHeaderSortableService.getSortValue({column, direction});
    this.setOrganizations();
  }

  private resetProperties() {
    this.cursors = new Map<number, string>();
    this.cursors.set(0, Cursor.INITIAL_CURSOR_REFERENCE);
    this.organizationsList = new Array<Organization>();
    this.paginateSettings = {
      ...this.paginateSettings,
      currentPage: 1
    };
  }

  private getRestrictedTenantInteraction(): void {
    this.organizationService.restrictedOrganizationInteraction().subscribe({
      next: (success) => {
        this.restrictOrganizationInteraction = success && success.length > 0 && success[0];
      }, error: (error) => {
        this.restrictOrganizationInteraction = false;
        const message = this.translation.instant('error determining if there is a restriction on user interaction.');
        this.notification.error(message, error, this.CURRENT_PAGE_NAME, false);
      }
    });
  }

  private setOrganizations(): void {
    if (this.elasticSearchService.isInvalidSearchTerm(this.searchTerm)) {
      this.organizationsList = [];
      this.organizationCount = 0;
      this.paginateSettings = {...this.paginateSettings, totalItems: this.organizationCount};
      return;
    }

    this.loadingIndicator.show(this.translation.instant('Loading organizations'));
    this.elasticSearchService.listOrganizations(this.searchTerm, this.sortValue, this.paginateSettings.currentPage, this.paginateSettings.itemsPerPage, this.rootOrganizationId)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: ({result}: ElasticSearchResponseEntity) => {
        this.organizationsList = result?.data || [];
        if (this.searchTerm) {
          this.organizationCount = result?.totalHits || 0;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.organizationCount};
        }
        this.loadingIndicator.hide();
      }, error: error => {
        const message = this.translation.instant('Error getting organizations list.');
        this.loadingIndicator.hide();
        this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
      }
    });

    if (!this.searchTerm) {
      this.organizationsCountForUser(this.rootOrganizationId).pipe(takeUntil(this.destroy$)).subscribe((count) => {
        if (count) {
          this.organizationCount = count.result.totalHits;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.organizationCount};
        }
      });
    }
  }

  private setAuthenticatedUserRoleAndOrganization() {
    this.loadingIndicator.show();
    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(takeUntil(this.destroy$), catchError(error => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
      return of(null);
    })).pipe(switchMap((user: User | null) => {
      let listObs = of(null);
      let countObserver = of(null);
      if (user?.parentId) {
        const userRole = new Role(user);
        this.rootOrganizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
        this.isSuperAdminRole = userRole.isSuperAdminRole;
        this.isAdminRole = userRole.isAdminRole || userRole.isTechAdminRole;

        listObs = this.elasticSearchService.listOrganizations(
            this.searchTerm,
            this.sortValue,
            this.paginateSettings.currentPage,
            this.paginateSettings.itemsPerPage,
            this.rootOrganizationId).pipe(startWith(null), catchError(error => {
          const message = this.translation.instant('Error getting organizations list.');
          this.loadingIndicator.hide();
          this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
          return of(null);
        }));

        countObserver = this.searchTerm ? of(null) : this.organizationsCountForUser(this.rootOrganizationId);

        return combineLatest([listObs, countObserver]);
      }
      return combineLatest([listObs, countObserver]);
    })).subscribe(([list, count]: [ElasticSearchResponseEntity | null, ElasticSearchResponseEntity | null]) => {
      if (list && !this.organizationsList.length) {
        this.organizationsList = list?.result?.data || [];
        if (this.searchTerm) {
          this.organizationCount = list.result.totalHits;
          this.paginateSettings = {...this.paginateSettings, totalItems: this.organizationCount};
        }
        this.loadingIndicator.hide();
      }
      if (count && !this.organizationCount) {
        this.organizationCount = count.result.totalHits;
        this.paginateSettings = {...this.paginateSettings, totalItems: this.organizationCount};
      }
    });
  }

  private organizationsCountForUser(rootOrganizationId: number | null): Observable<any> {
    return this.elasticSearchService.countOrganizations(rootOrganizationId)
    .pipe(startWith(null), catchError(err => {
      console.error(`${this.CURRENT_PAGE_NAME}. Error getting organizations count.`, JSON.stringify(err));
      return of(null);
    }));
  }
}
